/**
 * Offer picker — mirrors the Binom "Choose offer" modal: filter row
 * (group + country + AN + search) + sortable table sorted by ↓id. Country
 * and Aff. Network dropdowns mirror the Binom panel.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo, useState } from 'react';

import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/layout/Drawer';
import { Button } from '@/components/ui/layout/Button';
import { SearchableSelect } from '@/components/ui/form/input/SearchableSelect';
import {
  useBinomAffiliateNetworks,
  useBinomCountries,
  useBinomGroups,
  useBinomOffers,
  type BinomOffer,
} from '@/hooks/binom';

import type { BinomDraftItem } from '../../binom-flow-draft.types';

import BinomOfferPickerExtras from './BinomOfferPickerExtras';
import BinomOfferPickerTable from './picker-shared/tables/BinomOfferPickerTable';
import PickerFilterRow from './picker-shared/PickerFilterRow';
import { filterOfferRows } from './picker-shared/filter-offer-rows';
import { offerToDraftItem } from './picker-shared/offer-to-draft-item';
import {
  countryFilterOptions,
  groupFilterOptions,
  groupNameMap,
  networkFilterOptions,
} from './picker-shared/picker-filter-options';

export interface BinomOfferPickerProps {
  open: boolean;
  onClose: () => void;
  excludeIds: string[];
  onAttach: (items: BinomDraftItem[]) => void;
}

const BinomOfferPicker: FC<BinomOfferPickerProps> = ({
  open,
  onClose,
  excludeIds,
  onAttach,
}) => {
  const [search, setSearch] = useState('');
  const [groupUuid, setGroupUuid] = useState('');
  const [country, setCountry] = useState('');
  const [networkId, setNetworkId] = useState('');
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const offersQuery = useBinomOffers(search);
  const groupsQuery = useBinomGroups('offers');
  const countriesQuery = useBinomCountries();
  const networksQuery = useBinomAffiliateNetworks();

  const groupOptions = useMemo(() => groupFilterOptions(groupsQuery.data), [groupsQuery.data]);
  const countryOptions = useMemo(() => countryFilterOptions(countriesQuery.data), [countriesQuery.data]);
  const networkOptions = useMemo(() => networkFilterOptions(networksQuery.data), [networksQuery.data]);
  const groupNameByUuid = useMemo(() => groupNameMap(groupsQuery.data), [groupsQuery.data]);
  const rows = useMemo(
    () => filterOfferRows({
      list: Array.isArray(offersQuery.data) ? offersQuery.data : [],
      excludeIds, groupUuid, country, networkId,
    }),
    [offersQuery.data, excludeIds, groupUuid, country, networkId],
  );
  const selectedCount = Object.values(selected).filter(Boolean).length;
  const close = () => {
    setSelected({});
    setSearch('');
    setGroupUuid('');
    setCountry('');
    setNetworkId('');
    onClose();
  };
  const submit = () => {
    const picks: BinomOffer[] = rows.filter((o) => selected[o.id]);
    if (picks.length > 0) onAttach(picks.map(offerToDraftItem));
    close();
  };

  return (
    <Drawer open={open} onOpenChange={(o) => !o && close()}>
      <DrawerContent variant="center" width="w-[52rem]">
        <DrawerHeader>
          <DrawerTitle>Choose offer</DrawerTitle>
        </DrawerHeader>
        <DrawerBody>
          <PickerFilterRow
            primary={
              <SearchableSelect
                value={groupUuid}
                onChange={(v) => setGroupUuid(String(v))}
                options={groupOptions}
                placeholder="All groups"
              />
            }
            search={search}
            onSearchChange={setSearch}
            extras={
              <BinomOfferPickerExtras
                country={country}
                network={networkId}
                countryOptions={countryOptions}
                networkOptions={networkOptions}
                onCountryChange={setCountry}
                onNetworkChange={setNetworkId}
              />
            }
          />
          <BinomOfferPickerTable
            rows={rows}
            loading={offersQuery.isLoading}
            selected={selected}
            onToggle={(id, v) => setSelected((s) => ({ ...s, [id]: v }))}
            groupNameByUuid={groupNameByUuid}
          />
        </DrawerBody>
        <DrawerFooter>
          <Button type="button" variant="outline" onClick={close} size="sm">
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={selectedCount === 0}
            onClick={submit}
          >
            Apply {selectedCount > 0 ? `(${selectedCount})` : ''}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default BinomOfferPicker;
